import { oids } from "@/configs/Settings";
import { GetContract } from "@/services/fhir/contract/GetContract";
import { PostContract } from "@/services/fhir/contract/PostContract";
import {
  CRM_CONTRACT_IDENTIFIER_ALL,
  CRM_CONTRACT_IDENTIFIER_TRIAL,
  CRM_CONTRACT_TIER_SYSTEM,
  CRM_CONTRACT_TIERS,
} from "src-next/modules/crm-clinical/contracts/constants/crmContractTiers";

const CRM_TRIAL_MESSAGES = 20;

export type EnsureCrm20TrialResult =
  | { created: true }
  | {
      created: false;
      reason: "already_has_crm" | "crm20_exists" | "invalid_org" | "create_failed";
    };

/**
 * Normaliza CNPJ ou Organization.id FHIR para authority `Organization/{oids.cnpj}-{cnpj}`.
 * Evita POST duplicado quando CheckoutForm passa só o CNPJ.
 */
export function normalizeOrganizationIdForFhir(organizationId: string): string {
  const trimmed = organizationId.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith(`${oids.cnpj}-`)) {
    return trimmed;
  }
  return `${oids.cnpj}-${trimmed}`;
}

function buildOrganizationAuthorityReference(organizationId: string): string {
  return `Organization/${normalizeOrganizationIdForFhir(organizationId)}`;
}

async function fetchLatestContract(
  organizationId: string,
  identifier: string,
): Promise<boolean> {
  const normalized = normalizeOrganizationIdForFhir(organizationId);
  if (!normalized) return false;

  try {
    const bundle = await GetContract({
      organization: normalized,
      count: 1,
      sort: true,
      status: "executed",
      identifier,
    });
    return Boolean(bundle?.entry?.[0]?.resource);
  } catch (error) {
    console.error("[crmTrialContract] erro ao buscar contrato:", error);
    return false;
  }
}

/** Qualquer tier CRM executado (trial ou pago). */
export async function hasAnyCrmContract(organizationId: string): Promise<boolean> {
  return fetchLatestContract(organizationId, CRM_CONTRACT_IDENTIFIER_ALL);
}

/** Verificação específica do pacote trial crm20 (idempotência). */
export async function hasCrm20TrialContract(organizationId: string): Promise<boolean> {
  return fetchLatestContract(organizationId, CRM_CONTRACT_IDENTIFIER_TRIAL);
}

/**
 * Cria contrato trial com 20 mensagens (`term[0].identifier.value`).
 * Falha não propaga — retorna null para não invalidar assinatura pós-atendimento.
 */
export async function createCrm20TrialContract(
  organizationId: string,
): Promise<fhir5.Contract | null> {
  const normalized = normalizeOrganizationIdForFhir(organizationId);
  if (!normalized) return null;

  const now = new Date().toISOString();
  const contract = {
    resourceType: "Contract",
    status: "executed",
    identifier: [
      {
        system: CRM_CONTRACT_TIER_SYSTEM,
        value: CRM_CONTRACT_TIERS.TRIAL,
      },
    ],
    authority: [
      {
        reference: buildOrganizationAuthorityReference(organizationId),
      },
    ],
    applies: {
      start: now,
    },
    term: [
      {
        identifier: {
          value: String(CRM_TRIAL_MESSAGES),
        },
        applies: {
          start: now,
        },
        text: "Pacote trial CRM - 20 mensagens grátis",
      },
    ],
  };

  try {
    const created = await PostContract({ data: contract });
    return (created as fhir5.Contract) ?? null;
  } catch (error) {
    console.error("[crmTrialContract] erro ao criar contrato crm20:", error);
    return null;
  }
}

/**
 * Garante pacote trial após pagamento FluxZap (pós-atendimento).
 * Best-effort: não lança exceção.
 */
export async function ensureCrm20TrialAfterPostAttendancePayment(
  organizationId: string,
): Promise<EnsureCrm20TrialResult> {
  const trimmed = organizationId?.trim();
  if (!trimmed) {
    return { created: false, reason: "invalid_org" };
  }

  if (await hasAnyCrmContract(trimmed)) {
    return { created: false, reason: "already_has_crm" };
  }

  if (await hasCrm20TrialContract(trimmed)) {
    return { created: false, reason: "crm20_exists" };
  }

  const created = await createCrm20TrialContract(trimmed);
  if (created?.id) {
    return { created: true };
  }

  return { created: false, reason: "create_failed" };
}
